import { useState } from 'react'
import { Plus, Settings2, Play, Square } from 'lucide-react'
import { Header } from '@/components/layout/Header'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useSpreadStatus, useSpreadSettings, useSaveSpreadAlert, useDeleteSpreadAlert, useUpdateSpreadAlertsIgnore, useStartSpread, useStopSpread } from '@/hooks/useAlerts'
import { useAuth } from '@/hooks/useAuth'
import { StatsCard, ExchangeBadge, GlobalIgnoreList } from '@/components/common'
import { SpreadAlertEditSheet } from '@/components/alerts/SpreadAlertEditSheet'
import type { SpreadAlert } from '@/types'

export default function Spread() {
  const { data: status } = useSpreadStatus()
  const { data: settings, isLoading } = useSpreadSettings()
  const saveAlert = useSaveSpreadAlert()
  const deleteAlert = useDeleteSpreadAlert()
  const updateIgnore = useUpdateSpreadAlertsIgnore()
  const startSpread = useStartSpread()
  const stopSpread = useStopSpread()
  const { isAdmin } = useAuth()

  const [editingAlert, setEditingAlert] = useState<SpreadAlert | null>(null)
  const [isSheetOpen, setIsSheetOpen] = useState(false)

  const alerts = settings?.spread_alerts || []
  const isRunning = status?.running === true

  const handleAddAlert = () => {
    setEditingAlert(null)
    setIsSheetOpen(true)
  }

  const handleEditAlert = (alert: SpreadAlert) => {
    setEditingAlert(alert)
    setIsSheetOpen(true)
  }

  const handleSaveAlert = async (alertData: Partial<SpreadAlert>) => {
    await saveAlert.mutateAsync(alertData)
  }

  const handleDeleteAlert = async (id: string) => {
    await deleteAlert.mutateAsync(id)
  }

  const handleUpdateIgnore = async (symbols: string[]) => {
    await updateIgnore.mutateAsync(symbols)
  }

  return (
    <div className="flex flex-col">
      <Header title="Spread Alerts" />

      <div className="flex-1 space-y-6 p-6">
        {/* Header with Add Alert and Global Ignore */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant={isRunning ? 'success' : 'secondary'}>
              {isRunning ? 'Active' : 'Stopped'}
            </Badge>
            {isAdmin && (
              isRunning ? (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => stopSpread.mutate()}
                  disabled={stopSpread.isPending}
                >
                  <Square className="mr-2 h-3 w-3" />
                  {stopSpread.isPending ? 'Stopping...' : 'Stop'}
                </Button>
              ) : (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => startSpread.mutate()}
                  disabled={startSpread.isPending}
                >
                  <Play className="mr-2 h-3 w-3" />
                  {startSpread.isPending ? 'Starting...' : 'Start'}
                </Button>
              )
            )}
          </div>
          <div className="flex gap-2">
            <GlobalIgnoreList
              symbols={settings?.ignore || []}
              alertType="Spread alerts"
              onUpdate={handleUpdateIgnore}
              isUpdating={updateIgnore.isPending}
              compact
            />
            <Button variant="outline" onClick={handleAddAlert}>
              <Plus className="mr-2 h-4 w-4" />
              Add Alert
            </Button>
          </div>
        </div>

        {/* Info Banner */}
        <Card className="border-purple-500/30 bg-purple-500/10">
          <CardContent className="py-4">
            <p className="text-sm text-purple-300">
              <strong>What are Spread alerts?</strong> Tracks ongoing cross-exchange
              price differentials between spot and futures. Uses live-updating Telegram
              messages — editing the same message on each poll, and sending new reply
              notifications when spreads change significantly.
            </p>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-3">
          <StatsCard
            title="Status"
            value={isRunning ? 'Monitoring' : 'Stopped'}
            description="Cross-exchange spread tracking"
            status={isRunning ? 'running' : 'stopped'}
          />
          <StatsCard
            title="Active Triggers"
            value={status?.triggers_count || 0}
            description="Spread alerts triggered"
            status={isRunning ? 'running' : 'stopped'}
          />
          <StatsCard
            title="Configured Alerts"
            value={alerts.length}
            description="Active alert configurations"
          />
        </div>

        {/* Alerts List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Configured Alerts</CardTitle>
            <CardDescription>
              Cross-exchange spread monitoring configurations
            </CardDescription>
          </CardHeader>
          <CardContent>
            {alerts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No spread alerts configured. Click "Add Alert" to create one.
              </div>
            ) : (
              <div className="space-y-3">
                {alerts.map((alert, idx) => (
                  <div
                    key={alert.id || idx}
                    className="flex items-center justify-between rounded-lg border p-4"
                  >
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{alert.name || alert.id || `Alert ${idx + 1}`}</span>
                        <Badge variant={alert.enabled !== false ? 'success' : 'secondary'}>
                          {alert.enabled !== false ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(alert.exchanges || []).map((ex) => (
                          <ExchangeBadge key={ex} exchange={ex} />
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Min Spread: {alert.filters?.min_diff_pct || alert.min_diff_pct || 5}%
                        {' · '}
                        Update: {alert.filters?.spread_update_pct || alert.spread_update_pct || 10}%
                        {' · '}
                        Group Tol: {alert.filters?.spread_group_tolerance_pct || alert.spread_group_tolerance_pct || 5}%
                      </p>
                      {/* Tags */}
                      {(alert.tag_expression || (alert.required_tags?.length || 0) > 0 || (alert.exclude_tags?.length || 0) > 0) && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {alert.tag_expression ? (
                            <Badge variant="outline" className="text-xs font-mono">
                              {alert.tag_expression}
                            </Badge>
                          ) : (
                            <>
                              {(alert.required_tags || []).map((tag) => (
                                <Badge key={tag} className="bg-green-500/20 text-green-500 text-xs">
                                  +{tag}
                                </Badge>
                              ))}
                              {(alert.exclude_tags || []).map((tag) => (
                                <Badge key={tag} variant="destructive" className="text-xs">
                                  -{tag}
                                </Badge>
                              ))}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditAlert(alert)}
                      >
                        <Settings2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Sheet */}
      <SpreadAlertEditSheet
        open={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        alert={editingAlert}
        onSave={handleSaveAlert}
        onDelete={handleDeleteAlert}
      />
    </div>
  )
}
